import React, { useState, useEffect } from 'react';
import { Sparkles, LogIn, Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export function Landing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showSignIn, setShowSignIn] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔄 Starting auth check on Landing page');

      const authTimeout = setTimeout(() => {
        console.error('⚠️ Auth check timeout - forcing cleanup');
        supabase.auth.signOut().then(() => {
          localStorage.clear();
          setPageLoaded(true);
        });
      }, 20000);

      try {
        await supabase.auth.refreshSession();
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (session?.user?.id) {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profileError && profileError.code !== 'PGRST116') throw profileError;

          // If profile exists, navigate to dashboard
          if (profile) {
            clearTimeout(authTimeout);
            console.log('✅ Navigating to /dashgrowthloop once');
            navigate('/dashgrowthloop');
            return;
          }
          
          // If no profile exists but user is authenticated, try to create one
          if (!profile) {
            const { error: insertError } = await supabase
              .from('profiles')
              .insert([{ 
                id: session.user.id, 
                email: session.user.email || '', 
                name: session.user.user_metadata?.name || session.user.email || 'Friend' 
              }]);

            if (insertError) {
              console.error('❌ Failed to create profile in Landing:', insertError);
              await supabase.auth.signOut();
              localStorage.clear();
              setPageLoaded(true);
              return;
            }
            
            clearTimeout(authTimeout);
            console.log('✅ Profile created, navigating to /dashgrowthloop');
            navigate('/dashgrowthloop');
            return;
          }
        }
      } catch (error) {
        console.error('❌ Auth check error:', error);
        await supabase.auth.signOut();
        localStorage.clear();
      } finally {
        clearTimeout(authTimeout);
        setPageLoaded(true);
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });

      if (signInError || !data.session || !data.user) {
        setError("We couldn't sign you in. Please check your email and password, then try again.");
        setIsLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        setError("We couldn't find your profile. Please contact support.");
        setIsLoading(false);
        return;
      }

      // If profile doesn't exist, create one
      if (!profile) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert([{ 
            id: data.user.id, 
            email: data.user.email || '', 
            name: data.user.user_metadata?.name || data.user.email || 'Friend' 
          }]);

        if (insertError) {
          console.error('❌ Failed to create profile during sign in:', insertError);
          setError("We couldn't create your profile. Please contact support.");
          setIsLoading(false);
          return;
        }
      }

      navigate('/dashgrowthloop');
    } catch (err: any) {
      console.error('❌ Unexpected login error:', err);
      setError("Something went wrong. Please try again in a few minutes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setSuccess('Check your email for password reset instructions');
      setIsResettingPassword(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!pageLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-blue-50">
        <div className="text-[#236caf] text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-blue-50 p-6">
      <div className="max-w-2xl w-full flex flex-col items-center">
        <div className="flex justify-center mb-6">
          <img 
            src="https://storage.googleapis.com/msgsndr/uyhOpumyT9PNneWXZAK1/media/685de0f55c72d25d1830176d.png"
            alt="The Freelie App - Jump to your freedom and success."
            className="w-75 h-auto object-contain"
          />
        </div>

        {showSignIn ? (
          <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center gap-2 mb-6">
              <LogIn className="w-6 h-6 text-[#236caf]" />
              <h2 className="text-2xl font-bold text-[#004060]">Welcome Back</h2>
            </div>

            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">{error}</div>}
            {success && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm mb-4">{success}</div>}

            {isResettingPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading || !email}>
                  {isLoading ? 'Sending...' : 'Send Reset Instructions'}
                </Button>

                <button
                  type="button"
                  onClick={() => setIsResettingPassword(false)}
                  className="block w-full text-[#236caf] hover:text-[#004060] transition-colors duration-200"
                >
                  Back to Sign In
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>

                <div className="flex flex-col gap-2 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsResettingPassword(true);
                      setError('');
                      setSuccess('');
                    }}
                    className="text-[#236caf] hover:text-[#004060] transition-colors duration-200"
                  >
                    Forgot Password?
                  </button>

                  <button
                    type="button"
                   onClick={() => navigate('/registration')}
                    className="text-[#236caf] hover:text-[#004060] transition-colors duration-200"
                  >
                    Need an account? Get started
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <Button onClick={() => navigate('/introduction')} className="flex items-center gap-2 text-lg px-8">
              Get Started
              <ArrowRight className="w-5 h-5" />
            </Button>

            <button
              onClick={() => setShowSignIn(true)}
              className="text-[#236caf] hover:text-[#004060] transition-colors duration-200"
            >
              Already have an account? Sign in
            </button>
          </div>
        )}
      </div>

      <div className="mt-8 text-sm text-gray-500">
        <a href="https://www.freelie.net/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[#236caf] transition-colors duration-200">Terms of Service</a>
        <span className="mx-2">•</span>
        <a href="https://www.freelie.net/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[#236caf] transition-colors duration-200">Privacy Policy</a>
      </div>
    </div>
  );
}